import { MatchBundle } from './insightsQueries';

/**
 * Sample filters for Insights player maps (competition / calendar date range / match).
 * Per multi-select dimension: null = all, [] = none (Clear), otherwise the picked ids.
 * Dimensions AND together; values within a dimension OR (union).
 * Date range is inclusive on YYYY-MM-DD calendar days (dateFrom / dateTo).
 */

export interface MatchBundleFilterSelection {
    /** null = all competitions; [] = none; otherwise OR of ids. */
    competitionIds?: number[] | null;
    /** Inclusive range start as YYYY-MM-DD (local). Null/empty + empty dateTo = all dates. */
    dateFrom?: string | null;
    /** Inclusive range end as YYYY-MM-DD (local). */
    dateTo?: string | null;
    /** null = all matches; [] = none; otherwise OR of ids. */
    matchIds?: number[] | null;
}

export interface CompetitionFilterOption {
    id: number;
    name: string;
}

export interface DateFilterOption {
    key: string;
    label: string;
}

export interface MatchFilterOption {
    id: number;
    label: string;
    competitionId: number;
    dateKey: string;
}

export interface BundleFilterOptions {
    competitions: CompetitionFilterOption[];
    dates: DateFilterOption[];
    matches: MatchFilterOption[];
}

/** Local calendar day key for multi-select (stable, no timezone flip for display). */
export const matchDateKey = (startDate: Date | string | undefined): string | undefined => {
    if (!startDate) return undefined;
    const d = startDate instanceof Date ? startDate : new Date(startDate);
    if (Number.isNaN(d.getTime())) return undefined;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const MONTHS_SHORT = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

/** e.g. "12 Aug" (year when useful is left to the caller / date chip). */
export const formatMatchDayLabel = (startDate: Date | string | undefined): string => {
    if (!startDate) return 'Unknown date';
    const d = startDate instanceof Date ? startDate : new Date(startDate);
    if (Number.isNaN(d.getTime())) return 'Unknown date';
    return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
};

/** Label for a YYYY-MM-DD key without UTC flip (local calendar parts). */
export const formatDateKeyLabel = (dateKey: string): string => {
    const parts = dateKey.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return dateKey;
    const [, m, d] = parts;
    if (m < 1 || m > 12 || d < 1 || d > 31) return dateKey;
    return `${d} ${MONTHS_SHORT[m - 1]}`;
};

/** Trigger summary for date range: "All", "12 Aug", or "12 Aug - 20 Aug". */
export const dateRangeFilterSummary = (
    dateFrom?: string | null,
    dateTo?: string | null
): string => {
    if (!dateFrom && !dateTo) return 'All';
    if (dateFrom && dateTo) {
        if (dateFrom === dateTo) return formatDateKeyLabel(dateFrom);
        return `${formatDateKeyLabel(dateFrom)} - ${formatDateKeyLabel(dateTo)}`;
    }
    if (dateFrom) return formatDateKeyLabel(dateFrom);
    return formatDateKeyLabel(dateTo!);
};

export const competitionLabel = (
    competitionId: number | undefined,
    competitionDisplayName: string | undefined
): string => {
    if (competitionDisplayName && competitionDisplayName.trim())
        return competitionDisplayName.trim();
    if (competitionId != null) return `Competition ${competitionId}`;
    return 'Unknown competition';
};

export const opponentDisplayName = (
    detail: MatchBundle['detail'],
    ourTeamIds: number[]
): string => {
    const ourIsHome = ourTeamIds.includes(detail.homeTeamId);
    const name = ourIsHome ? detail.awayTeamDisplayName : detail.homeTeamDisplayName;
    return (name && String(name).trim()) || 'Opponent';
};

/** Readable match chip: "12 Aug · vs Jug". */
export const matchFilterLabel = (bundle: MatchBundle, ourTeamIds: number[]): string => {
    const day = formatMatchDayLabel(bundle.detail.startDate);
    return `${day} · vs ${opponentDisplayName(bundle.detail, ourTeamIds)}`;
};

/** null or full set = all in that dimension. [] = none (Clear). */
export const isAllFilterValues = <T>(selected: T[] | null, allCount: number): boolean =>
    selected === null || (allCount > 0 && selected.length === allCount);

export const isNoneFilterValues = <T>(selected: T[] | null): boolean =>
    selected !== null && selected.length === 0;

/**
 * Chip toggle: from All → solo that value; otherwise add/remove.
 * Clearing the last chip returns null (= all).
 */
export const toggleFilterValue = <T>(
    selected: T[] | null,
    value: T,
    allValues: T[]
): T[] | null => {
    if (allValues.length === 0) return null;
    if (isAllFilterValues(selected, allValues.length)) return [value];
    const cur = selected || [];
    if (cur.includes(value)) {
        const next = cur.filter((v) => v !== value);
        return next.length === 0 ? null : next;
    }
    const next = [...cur, value];
    return next.length === allValues.length ? null : next;
};

/**
 * Checkbox toggle: from All → uncheck one (everyone else stays); from None → check one;
 * otherwise add/remove. Full set normalizes to null (= all). Last uncheck → [] (none).
 */
export const toggleFilterCheckbox = <T>(
    selected: T[] | null,
    value: T,
    allValues: T[]
): T[] | null => {
    if (allValues.length === 0) return null;
    if (isAllFilterValues(selected, allValues.length)) {
        return allValues.filter((v) => v !== value);
    }
    if (isNoneFilterValues(selected)) {
        return [value];
    }
    const cur = selected || [];
    if (cur.includes(value)) {
        return cur.filter((v) => v !== value);
    }
    const next = [...cur, value];
    return next.length === allValues.length ? null : next;
};

/** Trigger summary: "All", "None", a single option label, or "N selected". */
export const filterSelectionSummary = <T>(
    selected: T[] | null,
    allCount: number,
    singleLabel?: string
): string => {
    if (isAllFilterValues(selected, allCount)) return 'All';
    if (isNoneFilterValues(selected)) return 'None';
    if (selected && selected.length === 1 && singleLabel) return singleLabel;
    return `${selected?.length ?? 0} selected`;
};

export const filterMatchBundles = (
    bundles: MatchBundle[],
    selection: MatchBundleFilterSelection = {}
): MatchBundle[] => {
    const { competitionIds, dateFrom, dateTo, matchIds } = selection;
    const from = dateFrom || null;
    const to = dateTo || null;
    const hasDateRange = Boolean(from || to);
    const compsActive = competitionIds !== null && competitionIds !== undefined;
    const matchesActive = matchIds !== null && matchIds !== undefined;
    if (!compsActive && !hasDateRange && !matchesActive) return bundles;

    return bundles.filter((bundle) => {
        if (compsActive) {
            if (competitionIds!.length === 0) return false;
            if (!competitionIds!.includes(bundle.detail.competitionId)) return false;
        }
        if (matchesActive) {
            if (matchIds!.length === 0) return false;
            if (!matchIds!.includes(bundle.detail.id)) return false;
        }
        if (hasDateRange) {
            const key = matchDateKey(bundle.detail.startDate);
            // Inclusive calendar-day range on YYYY-MM-DD keys (lexicographic = chronological).
            if (!key) return false;
            if (from && key < from) return false;
            if (to && key > to) return false;
        }
        return true;
    });
};

/** Distinct competitions / dates / matches from the loaded sample, newest match first. */
export const collectBundleFilterOptions = (
    bundles: MatchBundle[],
    ourTeamIds: number[]
): BundleFilterOptions => {
    const competitionById = new Map<number, CompetitionFilterOption>();
    const dateByKey = new Map<string, DateFilterOption>();
    const matches: MatchFilterOption[] = [];

    const sorted = [...bundles].sort((a, b) => {
        const ta = a.detail.startDate ? new Date(a.detail.startDate).getTime() : 0;
        const tb = b.detail.startDate ? new Date(b.detail.startDate).getTime() : 0;
        return tb - ta;
    });

    sorted.forEach((bundle) => {
        const competitionId = bundle.detail.competitionId;
        if (competitionId != null && !competitionById.has(competitionId)) {
            competitionById.set(competitionId, {
                id: competitionId,
                name: competitionLabel(competitionId, bundle.detail.competitionDisplayName),
            });
        }

        const dateKey = matchDateKey(bundle.detail.startDate);
        if (dateKey && !dateByKey.has(dateKey)) {
            dateByKey.set(dateKey, {
                key: dateKey,
                label: formatMatchDayLabel(bundle.detail.startDate),
            });
        }

        matches.push({
            id: bundle.detail.id,
            label: matchFilterLabel(bundle, ourTeamIds),
            competitionId: competitionId,
            dateKey: dateKey || '',
        });
    });

    const competitions = Array.from(competitionById.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
    );
    // Dates: newest first (keys YYYY-MM-DD sort lexicographically).
    const dates = Array.from(dateByKey.values()).sort((a, b) => b.key.localeCompare(a.key));

    return { competitions, dates, matches };
};

/** Match count label for panel heads: "0 matches" / "1 match" / "12 matches". */
export const matchCountLabel = (count: number): string =>
    count === 1 ? '1 match' : `${count} matches`;

/** Default filter bar: all competitions / dates / matches. */
export const emptyMatchBundleFilters = (): MatchBundleFilterSelection => ({
    competitionIds: null,
    dateFrom: null,
    dateTo: null,
    matchIds: null,
});
